// ═══════════════════════════════════════════════════════════════════════════════
// Prompt when user submits task / Home Stock while a comment is typed but not sent
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppButton } from '../ui/AppButton';
import { AppModal } from '../ui/AppModal';

export type UnsentCommentSubmitContext = 'taskComplete' | 'taskResubmit' | 'stockTake';

type UnsentCommentOnSubmitDialogProps = {
  open: boolean;
  context: UnsentCommentSubmitContext;
  busy: boolean;
  onBackToEdit: () => void;
  onSubmitOnly: () => void;
  onSubmitWithComment: () => void;
};

export const UnsentCommentOnSubmitDialog = ({
  open,
  context,
  busy,
  onBackToEdit,
  onSubmitOnly,
  onSubmitWithComment,
}: UnsentCommentOnSubmitDialogProps) => {
  const { t } = useLanguage();
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      primaryRef.current?.focus();
    }
  }, [open]);

  const descriptionKey =
    context === 'taskComplete'
      ? 'comment.unsentSubmit.descriptionTask'
      : context === 'stockTake'
        ? 'comment.unsentSubmit.descriptionStockTake'
        : 'comment.unsentSubmit.descriptionResubmit';

  const submitOnlyKey =
    context === 'taskComplete'
      ? 'comment.unsentSubmit.submitOnlyTask'
      : context === 'stockTake'
        ? 'comment.unsentSubmit.submitOnlyStockTake'
        : 'comment.unsentSubmit.submitOnlyResubmit';

  return (
    <AppModal
      open={open}
      onClose={onBackToEdit}
      title={t('comment.unsentSubmit.title')}
      titleId="unsent-comment-dialog-title"
      closeAriaLabel={t('comment.unsentSubmit.backToEdit')}
      hideCloseButton
      closeDisabled={busy}
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">{t(descriptionKey)}</p>
      <div className="mt-6 flex flex-col gap-2">
        <AppButton
          ref={primaryRef}
          variant="primary"
          size="lg"
          fullWidth
          disabled={busy}
          onClick={onSubmitWithComment}
          className="shadow"
        >
          {busy ? t('comment.unsentSubmit.working') : t('comment.unsentSubmit.submitWithComment')}
        </AppButton>
        <AppButton variant="secondary" size="lg" fullWidth disabled={busy} onClick={onSubmitOnly}>
          {t(submitOnlyKey)}
        </AppButton>
        <AppButton variant="ghost" size="lg" fullWidth disabled={busy} onClick={onBackToEdit}>
          {t('comment.unsentSubmit.backToEdit')}
        </AppButton>
      </div>
    </AppModal>
  );
};
